// Immutable Governance history browser. Opening a row fetches only that version's Markdown. An editable old
// component may be copied into the current editor; saving still uses the CURRENT optimistic base, creating a
// new version rather than restoring or mutating history.

import { useEffect, useState } from 'react';
import type { GovernanceClientApi } from '../governance-api.ts';
import type {
  GovernanceDocumentKey,
  GovernanceVersionSummary,
  GovernanceVersionView,
} from '../governance-projections.ts';
import { Btn, EmptyState, Panel } from '../primitives.tsx';
import { GovernanceMarkdown } from './GovernanceMarkdown.tsx';

interface GovernanceHistoryProps {
  readonly documentKey: GovernanceDocumentKey;
  readonly history: readonly GovernanceVersionSummary[];
  readonly api: GovernanceClientApi;
  readonly onLoadIntoEditor: (version: GovernanceVersionView) => void;
}

function GovernanceHistoryList({
  history,
  selectedVersionId,
  onOpen,
}: {
  readonly history: readonly GovernanceVersionSummary[];
  readonly selectedVersionId: string | undefined;
  readonly onOpen: (versionId: string) => void;
}): React.JSX.Element {
  return (
    <ol aria-label="Document version history" className="space-y-2">
      {history.map((version) => (
        <li key={version.versionId}>
          <button
            type="button"
            aria-pressed={selectedVersionId === version.versionId}
            data-version-id={version.versionId}
            onClick={() => onOpen(version.versionId)}
            className="w-full rounded-lg border border-border bg-surface px-4 py-3 text-left hover:border-border-strong"
          >
            <span className="flex items-center justify-between gap-3">
              <span className="text-sm font-semibold text-text">Version {version.version}</span>
              <span className="text-xs uppercase text-text-subtle">{version.origin}</span>
            </span>
            <span className="mt-1 block text-xs text-text-subtle">{version.componentLabel}</span>
            <span className="mt-1 block text-xs text-text-subtle">{version.createdAt}</span>
          </button>
        </li>
      ))}
    </ol>
  );
}

function GovernanceVersionPanel({
  selected,
  loading,
  failed,
  onLoadIntoEditor,
}: {
  readonly selected: GovernanceVersionView | null;
  readonly loading: boolean;
  readonly failed: boolean;
  readonly onLoadIntoEditor: (version: GovernanceVersionView) => void;
}): React.JSX.Element {
  return (
    <Panel className="min-h-96 px-5 py-4">
      {loading ? <p className="text-sm text-text-subtle">Loading version…</p> : null}
      {failed ? (
        <p role="alert" className="text-sm text-text-muted">
          This version could not be loaded.
        </p>
      ) : null}
      {selected !== null ? (
        <div data-open-version={selected.versionId}>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-text">Version {selected.version}</h3>
              <p className="mt-1 text-xs text-text-subtle">
                {selected.componentLabel} · {selected.createdAt}
              </p>
            </div>
            {selected.editable ? (
              <Btn kind="ghost" sm onClick={() => onLoadIntoEditor(selected)}>
                Load into editor
              </Btn>
            ) : null}
          </div>
          <GovernanceMarkdown markdown={selected.markdown} />
        </div>
      ) : !loading && !failed ? (
        <p className="text-sm text-text-subtle">Choose a version to inspect its Markdown.</p>
      ) : null}
    </Panel>
  );
}

export function GovernanceHistory({
  documentKey,
  history,
  api,
  onLoadIntoEditor,
}: GovernanceHistoryProps): React.JSX.Element {
  const [selected, setSelected] = useState<GovernanceVersionView | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setSelected(null);
    setLoadingId(null);
    setFailed(false);
  }, [documentKey]);

  const open = async (versionId: string): Promise<void> => {
    setLoadingId(versionId);
    setFailed(false);
    try {
      setSelected(await api.getGovernanceVersion(documentKey, versionId));
    } catch {
      setFailed(true);
    } finally {
      setLoadingId(null);
    }
  };

  if (history.length === 0) {
    return <EmptyState>No versions have been recorded for this document yet.</EmptyState>;
  }

  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(0,20rem)_minmax(0,1fr)]">
      <GovernanceHistoryList
        history={history}
        selectedVersionId={selected?.versionId}
        onOpen={(versionId) => void open(versionId)}
      />
      <GovernanceVersionPanel
        selected={selected}
        loading={loadingId !== null}
        failed={failed}
        onLoadIntoEditor={onLoadIntoEditor}
      />
    </div>
  );
}
